import React, { useState, useEffect } from "react";
import {
  Plus,
  FileText,
  Eye,
  Link,
  X,
  Check,
  Loader2,
} from "lucide-react";
import { BASE_URL } from "utils/constant";
import { taskApi } from "api/tasks/taskApi";
import { useAlert } from "components/UI_components/Alert";

const DocumentManager = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("Other");
  const [selectedUploadFile, setSelectedUploadFile] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [taskGroups, setTaskGroups] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [linking, setLinking] = useState(false);
  
 
  const { addAlert } = useAlert();
  const siteId = localStorage.getItem("site");
  const token = localStorage.getItem("access");

  const tabs = [
    "All",
    "Blueprints",
    "Contracts",
    "Invoices",
    "Reports",
    "Other",
  ];

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/task/get-all-site-documents/${siteId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch documents");
      const data = await response.json();
      setDocuments(data.data || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
      addAlert("Failed to load documents", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (siteId) {
      fetchDocuments();
    }
  }, [siteId]);

  const uploadDocument = async () => {
    if (!selectedUploadFile) {
      addAlert("Please choose a file to upload", "error");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("site_id", siteId);
      formData.append("file", selectedUploadFile);
      formData.append("category", uploadCategory || "Other");
      formData.append("labels", "");

      const response = await fetch(`${BASE_URL}/task/create-site-document/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to upload document");

      addAlert("Document uploaded successfully", "success");
      setShowUploadModal(false);
      setSelectedUploadFile(null);
      setUploadCategory("Other");
      fetchDocuments();
    } catch (error) {
      console.error("Error uploading document:", error);
      addAlert("Failed to upload document", "error");
    } finally {
      setUploading(false);
    }
  };

  const openLinkModal = async (docId) => {
    setSelectedDocId(docId);
    setShowLinkModal(true);
    setLoadingTasks(true);
    try {
      const groups = await taskApi.getAllTaskGroups();
      setTaskGroups(groups || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      addAlert("Failed to load tasks", "error");
    } finally {
      setLoadingTasks(false);
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleLinkSubmit = async () => {
    if (selectedTasks.length === 0) return;

    try {
      setLinking(true);
      const response = await fetch(`${BASE_URL}/task/allocate-document-to-task/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          document_id: selectedDocId,
          task_ids: selectedTasks,
        }),
      });

      if (!response.ok) throw new Error("Failed to link document");

      addAlert("Document linked to tasks successfully", "success");
      setShowLinkModal(false);
      setSelectedTasks([]);
      setSelectedDocId(null);
      fetchDocuments(); // Refresh to show updated links if backend returns them
    } catch (error) {
      console.error("Error linking document:", error);
      addAlert("Failed to link document", "error");
    } finally {
      setLinking(false);
    }
  };

  // Helper to get initials for avatar
  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

 

  

  return (
    <div className="flex h-full w-full bg-[#F5F7FA] p-4 gap-6 relative">

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col gap-6 overflow-hidden">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Project</span>
            <span>{">"}</span>
            <span className="font-medium text-gray-900">Document Manager</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab
                  ? "bg-[#3B0069] text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              {tab}
            </button>
            ))}
          </div>

          <div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="ml-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-sm text-[#3B0069] border border-[#3B0069] hover:bg-indigo-50 transition-colors"
            >
              <Plus size={14} />
              + New Doc
            </button>
          </div>
        </div>

        {/* Document List Header */}
        <div className="grid grid-cols-12 gap-3 px-4 py-2 text-sm font-medium text-gray-500">
          <div className="col-span-4">Document Name</div>
          <div className="col-span-2">Created By</div>
          <div className="col-span-2">Created On</div>
          <div className="col-span-1">Labels</div>
          <div className="col-span-1">Linked Tasks</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {/* Document List */}
        <div className="flex-1 overflow-y-auto flex flex-col gap-1">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-[#3B0069]" />
            </div>
          ) : documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-500">
              <FileText size={48} className="mb-2 opacity-20" />
              <p>No documents found</p>
            </div>
          ) : (
            documents.map((doc) => (
              <div
                key={doc.id}
                className="bg-white rounded-xl p-2 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="grid grid-cols-12 gap-2 items-center">
                  {/* Document Name */}
                  <div className="col-span-4 flex items-center gap-2 overflow-hidden">
                    <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                      <FileText size={16} />
                    </div>
                    <span className="font-medium text-gray-900 truncate" title={doc.file_name || doc.file}>
                      {doc.file_name || (doc.file ? doc.file.split("/").pop() : "Untitled")}
                    </span>
                  </div>

                  {/* Created By */}
                  <div className="col-span-2 flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center text-[8px] font-bold text-gray-600">
                      {doc.created_by_name ? (
                        <img
                          src={`https://ui-avatars.com/api/?name=${doc.created_by_name}&background=random`}
                          alt={doc.created_by_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        "?"
                      )}
                    </div>
                    <span className="text-sm text-gray-700 truncate">
                      {doc.created_by_name || "Unknown"}
                    </span>
                  </div>

                  {/* Created On */}
                  <div className="col-span-2 text-sm text-gray-600">
                    {formatDate(doc.created_at)}
                  </div>

                  {/* Labels */}
                  <div className="col-span-1">
                    <span className="text-xs text-gray-400">-</span>
                  </div>

                  {/* Linked Tasks */}
                  <div className="col-span-1">
                     <span className="text-xs text-gray-400">-</span>
                  </div>

                  {/* Actions */}
                  <div className="col-span-2 flex flex-col gap-1 items-end">
                    <div className="w-full flex items-center justify-end mb-0.5">
                      <a 
                        href={doc.file} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-3 py-1 bg-[#3B0069] text-white text-xs font-medium rounded-lg hover:bg-[#2a004b] transition-colors"
                      >
                        <Eye size={14} />
                        View
                      </a>
                    </div>
                    <div className="w-full flex items-center justify-end">
                      <button 
                        onClick={() => openLinkModal(doc.id)}
                        className="flex items-center justify-center gap-2 px-3 py-1 bg-white border border-[#3B0069] text-[#3B0069] text-xs font-medium rounded-lg hover:bg-indigo-50 transition-colors"
                      >
                        <Link size={14} />
                        Link
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Link Modal */}
      {showLinkModal && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col shadow-xl">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-lg text-gray-900">Link to Task</h3>
              <button 
                onClick={() => setShowLinkModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              {loadingTasks ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-[#3B0069]" />
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                   <p className="text-sm text-gray-500">Select tasks to link this document to:</p>
                   {taskGroups.map(group => (
                     <div key={group.id} className="flex flex-col gap-2">
                       <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{group.group_name}</h4>
                       <div className="pl-2 border-l-2 border-gray-100">
                          <TaskGroupList 
                            groupId={group.id} 
                            selectedTasks={selectedTasks} 
                            toggleTaskSelection={toggleTaskSelection} 
                          />
                       </div>
                     </div>
                   ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 flex justify-end gap-3">
              <button 
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleLinkSubmit}
                disabled={linking || selectedTasks.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-[#3B0069] hover:bg-[#2a004b] rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {linking && <Loader2 size={14} className="animate-spin" />}
                Link Documents
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md flex flex-col shadow-xl">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-lg text-gray-900">Upload New Document</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="p-4 flex flex-col gap-3">
              <label className="text-sm text-gray-700">Category</label>
              <select
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                className="border border-gray-200 rounded px-3 py-2 text-sm"
              >
                <option>Blueprints</option>
                <option>Contracts</option>
                <option>Invoices</option>
                <option>Reports</option>
                <option>Other</option>
              </select>

              <label className="text-sm text-gray-700">Choose file</label>
              <input
                type="file"
                onChange={(e) => setSelectedUploadFile(e.target.files?.[0] || null)}
                className="text-sm"
              />

              <div className="text-sm text-gray-500">{selectedUploadFile ? selectedUploadFile.name : "No file selected"}</div>
            </div>

            <div className="p-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => { setShowUploadModal(false); setSelectedUploadFile(null); setUploadCategory("Other"); }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={uploadDocument}
                disabled={uploading || !selectedUploadFile}
                className="px-4 py-2 text-sm font-medium text-white bg-[#3B0069] hover:bg-[#2a004b] rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {uploading && <Loader2 size={14} className="animate-spin" />}
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component to fetch and list tasks for a group
const TaskGroupList = ({ groupId, selectedTasks, toggleTaskSelection }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const data = await taskApi.getAllTasks(groupId);
        setTasks(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, [groupId]);

  if (loading) return <div className="text-xs text-gray-400 py-1">Loading tasks...</div>;
  if (tasks.length === 0) return <div className="text-xs text-gray-400 py-1">No tasks</div>;

  return (
    <div className="flex flex-col gap-1">
      {tasks.map(task => (
        <label key={task.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedTasks.includes(task.id) ? "bg-[#3B0069] border-[#3B0069]" : "border-gray-300 bg-white"}`}>
            {selectedTasks.includes(task.id) && <Check size={10} className="text-white" />}
          </div>
          <input 
            type="checkbox" 
            className="hidden" 
            checked={selectedTasks.includes(task.id)}
            onChange={() => toggleTaskSelection(task.id)}
          />
          <span className="text-sm text-gray-700">{task.task_name}</span>
        </label>
      ))}
    </div>
  );
};

export default DocumentManager;